// В.1.d: общая утилита перманентного баффа юнита «+atk/+armor/+hp до конца игры».
//
// Используется:
//   - Интендантом тыла (выдача Штурмовой винтовки и Тактического щита Рекрутам).
//   - В будущем — самими Действиями Штурмовая винтовка / Тактический щит, когда
//     они будут разыгрываться из руки в В.1.f.
//
// Семантика (по мемо балансу):
//   - atk:   +N к base.atk и +N к current.atk.
//   - armor: +N к persistentMaxArmor и persistentArmor (учитывается как
//            «дополнительный максимум брони») И синхронно +N к current.armor.
//            Восстановление брони в EOT учитывает persistentArmor.
//   - hp:    +N к base.hp (max_hp) И синхронно +N к current.hp.
//
// Бафф ПЕРМАНЕНТНЫЙ:
//   - Остаётся при перемещении, уроне, лечении, триггерах.
//   - Молчание самого юнита НЕ снимает бафф (он приклеен Действием/способностью
//     другой карты, не способностью самой цели).
//   - Возврат в руку (Офицер связи 1-я линия) откатывает к шаблону xlsx —
//     все permanentBuffs очищаются, base.* возвращаются к template.* (см.
//     resetCardToTemplate в ofitser_svyazi.js).
//
// Бафф добавляется в card.permanentBuffs для отслеживания и возможной
// инспекции из тестов.

export function applyPermanentBuff(state, card, delta = {}, sourceId = 'unknown', sourceLabel = '') {
  if (!card) return false;
  const atk = delta.atk | 0;
  const armor = delta.armor | 0;
  const hp = delta.hp | 0;
  if (atk === 0 && armor === 0 && hp === 0) return false;

  if (atk) {
    card.base.atk += atk;
    card.current.atk += atk;
  }
  if (armor) {
    card.persistentArmor    = (card.persistentArmor    || 0) + armor;
    card.persistentMaxArmor = (card.persistentMaxArmor || 0) + armor;
    card.current.armor += armor;
  }
  if (hp) {
    card.base.hp += hp;
    card.current.hp += hp;
  }

  if (!Array.isArray(card.permanentBuffs)) card.permanentBuffs = [];
  card.permanentBuffs.push({ id: sourceId, label: sourceLabel, atk, armor, hp });

  if (state?.log && sourceLabel) {
    const ownerName = state.players?.[card.ownerId]?.name || '?';
    const tag = `+${atk}/${armor}/${hp}`;
    state.log.add(`✨ ${sourceLabel} → ${card.name_ru} [${ownerName}]: ${tag} (постоянно)`);
  }
  return true;
}

// C.1: снять все permanent-баффы с указанным sourceId (или префиксом id).
// Используется для динамических аур (Подкрепление Глобала) где бафф
// пересчитывается при каждом изменении состава поля. Семантика
// удаления — обратная applyPermanentBuff:
//   - atk: -N от base.atk и current.atk (cap >= 0).
//   - armor: -N от persistentArmor / persistentMaxArmor; current.armor cap.
//   - hp: -N от base.hp; current.hp cap.
//
// Параметр matchSource — строка точного совпадения или функция
// (sourceId) => boolean.
export function removePermanentBuffsBySource(state, card, matchSource) {
  if (!card || !Array.isArray(card.permanentBuffs) || card.permanentBuffs.length === 0) return 0;
  const matcher = (typeof matchSource === 'function')
    ? matchSource
    : (id) => id === matchSource;

  const surviving = [];
  let removed = 0;
  for (const buff of card.permanentBuffs) {
    if (!matcher(buff.id)) { surviving.push(buff); continue; }
    if (buff.atk) {
      card.base.atk = Math.max(0, card.base.atk - buff.atk);
      card.current.atk = Math.max(0, card.current.atk - buff.atk);
    }
    if (buff.armor) {
      card.persistentArmor    = Math.max(0, (card.persistentArmor    || 0) - buff.armor);
      card.persistentMaxArmor = Math.max(0, (card.persistentMaxArmor || 0) - buff.armor);
      const newMax = card.maxArmorWithMods;
      if (typeof newMax === 'number' && card.current.armor > newMax) {
        card.current.armor = newMax;
      }
    }
    if (buff.hp) {
      card.base.hp = Math.max(0, card.base.hp - buff.hp);
      if (card.current.hp > card.base.hp) card.current.hp = card.base.hp;
    }
    removed++;
  }
  card.permanentBuffs = surviving;
  return removed;
}

// В.1.f-2-A: ВРЕМЕННЫЙ бафф со сроком жизни до начала указанного хода (Ф1).
// Семантика прикрепления — как у applyPermanentBuff (atk →base+current,
// armor → persistentMaxArmor+persistentArmor+current, hp → base+current).
// Снятие в начале Ф1 хода expiresAtTurn (см. expireTemporaryBuffs):
//   - atk: -N к base/current.
//   - armor: -N к persistentMaxArmor/persistentArmor; current.armor cap по новому max.
//   - hp: -N к base.hp; current.hp cap по новому max.
//
// Используется Пропагандой Глобала (+1/1/1 на 2 хода) и потенциально
// другими карточками с временным баффом.
export function applyTemporaryBuff(state, card, delta = {}, expiresAtTurn, sourceId = 'unknown', sourceLabel = '') {
  if (!card) return false;
  const atk = delta.atk | 0;
  const armor = delta.armor | 0;
  const hp = delta.hp | 0;
  if (atk === 0 && armor === 0 && hp === 0) return false;

  if (atk) {
    card.base.atk += atk;
    card.current.atk += atk;
  }
  if (armor) {
    card.persistentArmor    = (card.persistentArmor    || 0) + armor;
    card.persistentMaxArmor = (card.persistentMaxArmor || 0) + armor;
    card.current.armor += armor;
  }
  if (hp) {
    card.base.hp += hp;
    card.current.hp += hp;
  }

  if (!Array.isArray(card.temporaryBuffs)) card.temporaryBuffs = [];
  card.temporaryBuffs.push({ id: sourceId, label: sourceLabel, atk, armor, hp, expiresAtTurn });

  if (state?.log && sourceLabel) {
    const ownerName = state.players?.[card.ownerId]?.name || '?';
    const tag = `+${atk}/${armor}/${hp}`;
    state.log.add(`⏳ ${sourceLabel} → ${card.name_ru} [${ownerName}]: ${tag} до начала хода ${expiresAtTurn}`);
  }
  return true;
}

// В.1.f-2-A: снятие истёкших временных баффов. Вызывается в начале Ф1 ДО
// applyOverloadDeaths и других триггеров. Идёт по обоим игрокам, по Героям
// и юнитам на поле.
//
// Алгоритм:
//   - atk: -N к base.atk и current.atk.
//   - armor: -N к persistentMaxArmor / persistentArmor; current.armor cap.
//   - hp: -N к base.hp; current.hp cap.
//   Юнит может умереть в момент cap (если current.hp > новый max и >0 уже
//   быть не может — но если current был выше нового max после урона, то
//   тут юнит остаётся жив; единственный путь смерти — current.hp выше
//   max уже не может, max опускается, а current.hp уже ниже max →
//   ничего не происходит). На практике юнит может оказаться на 0 hp
//   только если current был на новом max И буф снимает hp >= max нового.
//   Brief: «может умереть в момент снятия, если уже был ранен — это норма».
export function expireTemporaryBuffs(state) {
  for (const p of state.players) {
    const all = [];
    if (p.hero) all.push(p.hero);
    for (const u of p.unitsOnField()) all.push(u);
    for (const card of all) {
      if (!Array.isArray(card.temporaryBuffs) || card.temporaryBuffs.length === 0) continue;
      const surviving = [];
      for (const buff of card.temporaryBuffs) {
        if (state.currentTurn < buff.expiresAtTurn) {
          surviving.push(buff);
          continue;
        }
        // Снимаем.
        if (buff.atk) {
          card.base.atk = Math.max(0, card.base.atk - buff.atk);
          card.current.atk = Math.max(0, card.current.atk - buff.atk);
        }
        if (buff.armor) {
          card.persistentArmor    = Math.max(0, (card.persistentArmor    || 0) - buff.armor);
          card.persistentMaxArmor = Math.max(0, (card.persistentMaxArmor || 0) - buff.armor);
          // Cap current.armor по новому max.
          const newMax = card.maxArmorWithMods;
          if (card.current.armor > newMax) card.current.armor = newMax;
        }
        if (buff.hp) {
          card.base.hp = Math.max(0, card.base.hp - buff.hp);
          if (card.current.hp > card.base.hp) card.current.hp = card.base.hp;
        }
        if (state.log) {
          const ownerName = state.players?.[card.ownerId]?.name || '?';
          state.log.add(`⏳ ${buff.label} с ${card.name_ru} [${ownerName}]: бафф истёк`);
        }
      }
      card.temporaryBuffs = surviving;
    }
  }
}
